import React, { useState, useEffect } from 'react';
import imageCompression from 'browser-image-compression';

import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  InputNumber, 
  message, 
  Card, 
  Typography, 
  Popconfirm, 
  Select, 
  Switch, 
  Tag,
  Row,
  Col,
  Upload  // 添加 Upload 组件
} from 'antd';
import { 
  PrinterOutlined, 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined, 
  EyeOutlined,
  HeartOutlined,
  FireOutlined,
  UploadOutlined,  // 添加 UploadOutlined 图标
  DeleteOutlined as DeleteIcon
} from '@ant-design/icons';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
// 图片压缩选项
const compressionOptions = {
  maxSizeMB: 1,           // 压缩后最大文件大小（MB）
  maxWidthOrHeight: 1920, // 最大宽度或高度
  useWebWorker: true,     // 使用WebWorker提高性能
  fileType: 'image/jpeg', // 输出格式
  initialQuality: 0.8,    // 初始质量（0-1）
};


interface PrintedProduct {
  id: number;
  title: string;
  description?: string;
  status: string;
  category: string;
  difficulty_level?: string;
  visibility?: string;
  license_type?: string;
  print_quality?: string;
  bed_adhesion_type?: string;
  images?: string[];
  tags?: string[];
  layer_height?: number;
  material_consumption?: number;
  print_duration?: number;
  print_temperature?: number;
  infill_density?: number;
  print_speed?: number;
  dimension_length?: number;
  dimension_width?: number;
  dimension_height?: number;
  weight?: number;
  volume?: number;
  estimated_cost?: number;
  support_structure?: boolean;
  is_featured?: boolean;
  like_count?: number;
  view_count?: number;
  favorite_count?: number;
  download_count?: number;
  material_id?: number;
  printer_id?: number;
  created_at?: string;
  makeworld_url?: string;  // 新增字段（仅前端展示）
}

const PrintedProductManagement: React.FC = () => {
  const [products, setProducts] = useState<PrintedProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PrintedProduct | null>(null);
  const [form] = Form.useForm();
  const [uploading, setUploading] = useState(false);  // 添加上传状态

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api/printed_products', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setProducts(data);
      } else {
        // 处理HTTP错误响应
        const errorData = await response.json();
        const errorMessage = errorData.detail || errorData.message || '获取成品列表失败';
        message.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } catch (error) {
      console.error('获取成品列表失败:', error);
      message.error('获取成品列表失败');
    } finally {
      setLoading(false);
    }
  };


  const handleImageUpload = async (file: File) => {
    try {
      // 压缩图片
      const compressedFile = await imageCompression(file, compressionOptions);
      
      // 检查压缩后的大小
      if (compressedFile.size > 5 * 1024 * 1024) {
        message.error('图片压缩后仍超过5MB，请选择更小的图片');
        return null;
      }
      
      console.log('压缩前后对比:');
      console.log(`原始大小: ${(file.size / 1024 / 1024).toFixed(2)} MB`);
      console.log(`压缩后大小: ${(compressedFile.size / 1024 / 1024).toFixed(2)} MB`);
      
      const formData = new FormData();
      formData.append('file', compressedFile, file.name);
      
      const token = localStorage.getItem('admin_token');
      const response = await fetch('/api_upload', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });
      const result = await response.json();
      console.log('上传API返回结果:', result);
      
      if (result.status === 0 && result.data?.url) {
        // 获取当前的图片列表
        let currentImages = form.getFieldValue('images') || [];
        if (typeof currentImages === 'string') {
          currentImages = [];
        }
        console.log('当前图片列表:', currentImages);
        // 添加新的图片URL
        const newImages = [...currentImages, result.data.url];
        console.log('新图片列表:', newImages);
        form.setFieldsValue({ images: newImages }); 
        message.success('图片上传成功');
        return result.data.url;
      } else {
        message.error(result.msg || '上传失败');
        return null;
      }
    } catch (error) {
      console.error('图片压缩或上传失败:', error);
      message.error('处理失败，请重试');
      return null;
    }
  };

  // // 添加图片上传处理函数
  // const handleImageUpload = async (file: File) => {
  //   const formData = new FormData();
  //   formData.append('file', file);
    
  //   setUploading(true);
  //   try {
  //     const token = localStorage.getItem('admin_token');
  //     const response = await fetch('/api_upload', {
  //       method: 'POST',
  //       headers: {
  //         'Authorization': `Bearer ${token}`,
  //       },
  //       body: formData,
  //     });
      
  //     const result = await response.json();
  //     console.log('上传API返回结果:', result);
      
  //     if (result.status === 0 && result.data?.url) {
  //       // 获取当前的图片列表
  //       let currentImages = form.getFieldValue('images') || [];
  //       if (typeof currentImages === 'string') {
  //         currentImages = [];
  //       }
  //       console.log('当前图片列表:', currentImages);
  //       // 添加新的图片URL
  //       const newImages = [...currentImages, result.data.url];
  //       console.log('新图片列表:', newImages);
  //       form.setFieldsValue({ images: newImages }); 
  //       message.success('图片上传成功');
  //       return result.data.url;
  //     } else {
  //       message.error(result.msg || '上传失败');
  //       return null;
  //     }
  //   } catch (error) {
  //     console.error('上传图片失败:', error);
  //     message.error('上传失败');
  //     return null;
  //   } finally {
  //     setUploading(false);
  //   }
  // };

  // 添加图片删除处理函数
  const handleImageRemove = (url: string) => {
    const currentImages = form.getFieldValue('images') || [];
    const newImages = currentImages.filter((img: string) => img !== url);
    form.setFieldsValue({ images: newImages });
  };

  // 添加自定义上传请求
  const customRequest = async ({ file, onSuccess, onError }: any) => {
    setUploading(true);
    try {
      const url = await handleImageUpload(file);
      if (url) {
        // Ant Design Upload组件期望onSuccess接收一个结果对象
        // 传递完整的响应对象，模拟API返回的结构
        onSuccess({
          response: {
            status: 0,
            msg: "上传成功",
            data: { url }
          }
        });
      } else {
        onError(new Error('上传失败'));
      }
    } catch (error) {
      onError(error);
    } finally {
      setUploading(false);
    }
  };

  // 添加上传前验证
  const beforeUpload = (file: File) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return false;
    }
    
    const isLt5M = file.size / 1024 / 1024 < 5;
    if (!isLt5M) {
      message.error('图片大小不能超过5MB!');
      return false;
    }
    
    return true;
  };

  // 批量上传图片
  const handleBatchUpload = async (files: File[]) => {
    setUploading(true);
    const uploadedUrls: string[] = [];
    
    try {
      for (const file of files) {
        const url = await handleImageUpload(file);
        if (url) {
          uploadedUrls.push(url);
        }
      }
      
      if (uploadedUrls.length > 0) {
        // 获取当前的图片列表
        let currentImages = form.getFieldValue('images') || [];
        if (typeof currentImages === 'string') {
          currentImages = [];
        }
        // 添加所有新的图片URL
        const newImages = [...currentImages, ...uploadedUrls];
        form.setFieldsValue({ images: newImages });
        message.success(`成功上传 ${uploadedUrls.length} 张图片`);
      }
    } catch (error) {
      console.error('批量上传失败:', error);
      message.error('批量上传失败');
    } finally {
      setUploading(false);
    }
  };

  const handleAdd = () => {
    setEditingProduct(null);
    form.resetFields();
    // 设置默认值
    form.setFieldsValue({
      status: 'published',
      category: 'decorative',
      difficulty_level: 'medium',
      visibility: 'public',
      license_type: 'personal_use',
      print_quality: 'medium',
      bed_adhesion_type: 'none',
      layer_height: 0.2,
      material_consumption: 0,
      print_duration: 120,  // 默认2小时（120分钟）
      print_temperature: 210,  // PLA常用温度
      infill_density: 20,
      print_speed: 60,
      dimension_length: 0,
      dimension_width: 0,
      dimension_height: 0,
      weight: 0,
      volume: 0,
      estimated_cost: 0,
      support_structure: false,
      is_featured: false,
      like_count: Math.floor(Math.random() * 50),  // 0-50随机数
      view_count: Math.floor(Math.random() * 50),  // 0-50随机数
      favorite_count: 0,
      download_count: 0,
      images: [],
      tags: [],
      makeworld_url: '',  // 新增字段
    });
    setModalVisible(true);
  };

  const handleEdit = (product: PrintedProduct) => {
    setEditingProduct(product);
    form.setFieldsValue({
      ...product,
      // 确保images是数组，如果不是则转换为空数组
      images: Array.isArray(product.images) ? product.images : [],
      tags: product.tags || []
    });
    setModalVisible(true);
  };

  const handleDelete = async (id: number) => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`/api/printed_products/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });
      
      if (response.ok) {
        message.success('删除成功');
        fetchProducts();
      } else {
        const errorData = await response.json();
        const errorMessage = errorData.detail || errorData.message || '删除失败';
        message.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
      }
    } catch (error) {
      console.error('删除成品失败:', error);
      message.error('删除失败');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const token = localStorage.getItem('admin_token');

      // 排除 makeworld_url 字段（后端不支持）
      const { makeworld_url, ...submitData } = values;

      let response;
      if (editingProduct) {
        // 更新成品
        response = await fetch(`/api/printed_products/${editingProduct.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submitData),
        });
      } else {
        // 创建成品
        response = await fetch('/api/printed_products', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(submitData),
        });
      }
      
      if (response.ok) {
        message.success(editingProduct ? '更新成功' : '创建成功');
        setModalVisible(false);
        fetchProducts();
      } else {
        const errorData = await response.json();
        // 处理422验证错误（FastAPI返回的验证错误格式）
        if (response.status === 422 && Array.isArray(errorData.detail)) {
          const errorMessages = errorData.detail.map((err: any) => {
            const field = err.loc?.join('.') || '未知字段';
            return `${field}: ${err.msg}`;
          }).join('; ');
          message.error(`验证失败: ${errorMessages}`);
        } else {
          // 处理其他错误
          const errorMessage = errorData.detail || errorData.message || '操作失败';
          message.error(typeof errorMessage === 'string' ? errorMessage : JSON.stringify(errorMessage));
        }
      }
    } catch (error) {
      console.error('保存成品失败:', error);
      message.error('保存失败');
    }
  };


  // 修改图片预览组件
  const ImagePreview: React.FC<{ images: any }> = ({ images }) => {
    // 确保images是数组，如果不是则转换为空数组
    const imageArray = Array.isArray(images) ? images : [];
    console.log(images);
    if (imageArray.length === 0) {
      return <Text type="secondary">暂无图片</Text>;
    }
    
    return (
      <Space wrap>
        {imageArray.map((url, index) => (
          <div key={index} style={{ position: 'relative' }}>
            <img 
              src={url} 
              alt={`成品图片 ${index + 1}`}
              style={{ 
                width: 60, 
                height: 60, 
                objectFit: 'cover',
                borderRadius: 4,
                border: '1px solid #f0f0f0'
              }}
            />
            <Button
              type="text"
              danger
              size="small"
              icon={<DeleteIcon />}
              style={{
                position: 'absolute',
                top: -8,
                right: -8,
                zIndex: 1,
                backgroundColor: 'white',
                borderRadius: '50%',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
              }}
              onClick={() => handleImageRemove(url)}
            />
          </div>
        ))}
      </Space>
    );
  };



  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'orange';
      case 'archived': return 'red';
      default: return 'default';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'published': return '已发布';
      case 'draft': return '草稿';
      case 'archived': return '已归档';
      default: return status;
    }
  };

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'decorative': return '#d32f2f';
      case 'functional': return '#1890ff';
      case 'toy': return '#ff9800';
      case 'art': return '#9c27b0';
      case 'tool': return '#795548';
      case 'other': return '#607d8b';
      default: return '#795548';
    }
  };

  const getCategoryText = (category: string) => {
    switch (category) {
      case 'decorative': return '装饰品';
      case 'functional': return '功能件';
      case 'toy': return '玩具';
      case 'art': return '艺术品';
      case 'tool': return '工具';
      case 'other': return '其他';
      default: return category;
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 60,
    },
    {
      title: '标题',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: PrintedProduct) => (
        <Space direction="vertical" size={2}>
          <Text strong>{text}</Text>
          {record.is_featured && (
            <Tag icon={<FireOutlined />} color="red" style={{ fontSize: '10px', padding: '0 4px' }}>
              精选
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={getStatusColor(status)}>
          {getStatusText(status)}
        </Tag>
      ),
    },
    {
      title: '分类',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => (
        <Tag color={getCategoryColor(category)}>
          {getCategoryText(category)}
        </Tag>
      ),
    },
    {
      title: '互动数据',
      key: 'interaction',
      render: (_: any, record: PrintedProduct) => (
        <Space size="small" direction="vertical">
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <HeartOutlined style={{ color: '#d32f2f', fontSize: '12px' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.like_count || 0}</Text>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <EyeOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
            <Text type="secondary" style={{ fontSize: '12px' }}>{record.view_count || 0}</Text>
          </div>
        </Space>
      ),
    },
    {
      title: '打印信息',
      key: 'print_info',
      render: (_: any, record: PrintedProduct) => (
        <Space direction="vertical" size={2}>
          {record.print_duration && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              时长: {Math.floor(record.print_duration / 60)}h
            </Text>
          )}
          {record.material_consumption && (
            <Text type="secondary" style={{ fontSize: '12px' }}>
              耗材: {record.material_consumption}g
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date: string) => (
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {date ? new Date(date).toLocaleDateString() : '-'}
        </Text>
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: PrintedProduct) => (
        <Space size="small">
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个成品吗？"
            description="删除后将无法恢复。"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
          <Title level={4}>3D打印成品管理</Title>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
          >
            添加成品
          </Button>
        </div>
        
        <Table
          columns={columns}
          dataSource={products}
          rowKey="id"
          loading={loading}
          pagination={{ pageSize: 10 }}
          scroll={{ x: 'max-content' }}
          style={{ overflowX: 'auto' }}
        />
      </Card>

      <Modal
        title={editingProduct ? '编辑成品' : '添加成品'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={() => setModalVisible(false)}
        width={window.innerWidth < 768 ? '100%' : 800}
        style={{ top: 20, maxWidth: '100vw' }}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ 
            status: 'draft',
            category: 'decorative',
            difficulty_level: 'medium',
            visibility: 'public',
            license_type: 'personal_use',
            print_quality: 'medium',
            bed_adhesion_type: 'none',
            layer_height: 0.2,
            infill_density: 20,
            print_speed: 60,
            support_structure: false,
            is_featured: false,
            like_count: 0,
            view_count: 0,
            favorite_count: 0,
            download_count: 0,
            images: []  // 添加默认图片数组
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="title"
                label="标题"
                rules={[{ required: true, message: '请输入标题' }]}
              >
                <Input placeholder="例如：创意花瓶、齿轮模型" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="status"
                label="状态"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Option value="draft">草稿</Option>
                  <Option value="published">已发布</Option>
                  <Option value="archived">已归档</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="description"
            label="描述"
          >
            <TextArea 
              rows={3} 
              placeholder="描述这个3D打印成品的特色、用途等" 
            />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="category"
                label="分类"
                rules={[{ required: true, message: '请选择分类' }]}
              >
                <Select>
                  <Option value="decorative">装饰品</Option>
                  <Option value="functional">功能件</Option>
                  <Option value="toy">玩具</Option>
                  <Option value="art">艺术品</Option>
                  <Option value="tool">工具</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="difficulty_level"
                label="难度级别"
              >
                <Select>
                  <Option value="easy">简单</Option>
                  <Option value="medium">中等</Option>
                  <Option value="hard">困难</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="visibility"
                label="可见性"
              >
                <Select>
                  <Option value="public">公开</Option>
                  <Option value="private">私有</Option>
                  <Option value="unlisted">未列出</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="license_type"
                label="许可类型"
              >
                <Select>
                  <Option value="personal_use">个人使用</Option>
                  <Option value="commercial_use">商业使用</Option>
                  <Option value="creative_commons">知识共享</Option>
                  <Option value="open_source">开源</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="print_quality"
                label="打印质量"
              >
                <Select>
                  <Option value="high">高</Option>
                  <Option value="medium">中</Option>
                  <Option value="low">低</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="bed_adhesion_type"
                label="床面附着类型"
              >
                <Select>
                  <Option value="none">无</Option>
                  <Option value="brim">边缘</Option>
                  <Option value="raft">底座</Option>
                  <Option value="skirt">裙边</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          {/* 修改图片上传部分 */}
          <Form.Item
            name="images"
            label="产品图片"
            help="支持JPG、PNG、GIF、WebP格式，单张图片不超过5MB"
          >
            <div>
              {/* 图片预览 */}
              <Form.Item noStyle shouldUpdate>
                {() => {
                  const images = form.getFieldValue('images') || [];
                  return <ImagePreview images={images} />;
                }}
              </Form.Item>
              
              {/* 上传组件 */}
              <Upload
                name="file"
                listType="picture-card"
                showUploadList={{
                  showPreviewIcon: true,
                  showRemoveIcon: true,
                }}
                customRequest={customRequest}
                beforeUpload={beforeUpload}
                accept="image/*"
                disabled={uploading}
                style={{ marginTop: 8 }}
                fileList={[]}  // 不显示文件列表，因为图片已经通过预览组件显示
                multiple={true}  // 支持多文件选择
              >
                <Button 
                  icon={<UploadOutlined />} 
                  loading={uploading}
                  disabled={uploading}
                >
                  {uploading ? '上传中...' : '上传图片'}
                </Button>
              </Upload>
              
              
            </div>
          </Form.Item>

          <Form.Item
            name="tags"
            label="标签"
          >
            <Select
              mode="tags"
              placeholder="输入标签，按回车添加"
              style={{ width: '100%' }}
            />
          </Form.Item>

          <Form.Item
            name="makeworld_url"
            label="MakeWorld URL"
            help="MakeWorld 模型链接（仅前端记录）"
          >
            <Input placeholder="https://makerworld.com/..." />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="layer_height"
                label="层高"
              >
                <InputNumber
                  min={0.01}
                  max={1.0}
                  step={0.01}
                  style={{ width: '100%' }}
                  placeholder="mm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="material_consumption"
                label="材料消耗"
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="g"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="print_duration"
                label="打印时长"
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="分钟"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="print_temperature"
                label="打印温度"
              >
                <InputNumber
                  min={0}
                  max={300}
                  step={5}
                  style={{ width: '100%' }}
                  placeholder="°C"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="infill_density"
                label="填充密度"
              >
                <InputNumber
                  min={0}
                  max={100}
                  step={5}
                  style={{ width: '100%' }}
                  placeholder="%"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="print_speed"
                label="打印速度"
              >
                <InputNumber
                  min={0}
                  max={300}
                  step={10}
                  style={{ width: '100%' }}
                  placeholder="mm/s"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="dimension_length"
                label="长度"
              >
                <InputNumber
                  min={0}
                  max={1000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="mm"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="dimension_width"
                label="宽度"
              >

            <InputNumber
                  min={0}
                  max={1000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="mm"
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="dimension_height"
                label="高度"
              >
                <InputNumber
                  min={0}
                  max={1000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="mm"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="weight"
                label="重量"
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="g"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="volume"
                label="体积"
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={1}
                  style={{ width: '100%' }}
                  placeholder="cm³"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="estimated_cost"
                label="预估成本"
              >
                <InputNumber
                  min={0}
                  max={10000}
                  step={0.1}
                  style={{ width: '100%' }}
                  placeholder="元"
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="support_structure"
                label="支撑结构"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="is_featured"
                label="精选作品"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="like_count"
                label="点赞数"
              >
                <InputNumber
                  min={0}
                  max={100000}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="view_count"
                label="浏览量"
              >
                <InputNumber
                  min={0}
                  max={1000000}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Form.Item
                name="favorite_count"
                label="收藏数"
              >
                <InputNumber
                  min={0}
                  max={100000}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="download_count"
                label="下载数"
              >
                <InputNumber
                  min={0}
                  max={100000}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="material_id"
                label="材料ID"
              >
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                name="printer_id"
                label="打印机ID"
              >
                <InputNumber
                  min={0}
                  step={1}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
};

export default PrintedProductManagement;
